import type MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';

/** Shared author controls and rendering contract; arbitrary sizes are never accepted. */
export const QUESTION_TEXT_SIZES = [
  { name: 'smallest', size: 16, headingLevel: 6 },
  { name: 'small', size: 20, headingLevel: 5 },
  { name: 'medium', size: 24, headingLevel: 4 },
  { name: 'large', size: 28, headingLevel: 3 },
  { name: 'larger', size: 32, headingLevel: 2 },
  { name: 'largest', size: 36, headingLevel: 1 },
] as const;

export type QuestionTextSizeName = typeof QUESTION_TEXT_SIZES[number]['name'];

export const QUESTION_CUSTOM_TEXT_SIZE_MIN = 12;
export const QUESTION_CUSTOM_TEXT_SIZE_MAX = 72;

/** Numeric suffixes are canonical integers, never authored CSS values. */
export const getQuestionCustomTextSizePx = (value: string): number | undefined => {
  if (!/^[1-9][0-9]?px$/.test(value)) return undefined;
  const size = Number(value.slice(0, -2));
  return size >= QUESTION_CUSTOM_TEXT_SIZE_MIN && size <= QUESTION_CUSTOM_TEXT_SIZE_MAX ? size : undefined;
};

/** Accepts a class suffix such as 'small' or '22px' for parser/editor reuse. */
export const isQuestionTextSize = (value: string): boolean =>
  QUESTION_TEXT_SIZES.some(({ name }) => name === value) || getQuestionCustomTextSizePx(value) !== undefined;

// Match the backend parser. Custom blocks are paired after native parsing, so
// they need their own bound to keep authored nesting safe for React rendering.
export const QUESTION_MARKDOWN_MAX_NESTING = 100;

const names = QUESTION_TEXT_SIZES.map(({ name }) => name).join('|') + '|[1-9][0-9]?px';
const inlineAttribute = new RegExp(`^\\{\\.text-(${names})\\}`);
const blockAttribute = new RegExp(`^:::[ \\t]*\\{\\.text-(${names})\\}[ \\t]*$`);
const blockOpener = /^:::[ \t]*\{[^\n]*$/;
const blockCloser = /^:::[ \t]*$/;

/**
 * Native tokens preserve Markdown's escaping/code/link rules. Block markers are
 * paired after native block parsing, so a ::: inside fenced code or another
 * list/quote cannot close a surrounding container accidentally.
 */
export const installQuestionTextSizes = (markdown: MarkdownIt): void => {
  markdown.inline.ruler.before('link', 'question_size_inline', (state, silent) => {
    // A size span is not a link: leave bracket accounting to native validation
    // so a span inside an ordinary link label is not rejected as a nested link.
    if (silent || state.src[state.pos] !== '[') return false;
    const start = state.pos;
    const end = state.md.helpers.parseLinkLabel(state, start, false);
    if (end < 0) return false;
    const attribute = inlineAttribute.exec(state.src.slice(end + 1, state.posMax));
    if (!attribute || !isQuestionTextSize(attribute[1])) return false;
    const max = state.posMax;
    const opening = state.push('question_size_inline_open', 'span', 1);
    opening.attrSet('class', `text-${attribute[1]}`);
    state.pos = start + 1;
    state.posMax = end;
    state.md.inline.tokenize(state);
    state.push('question_size_inline_close', 'span', -1);
    state.pos = end + 1 + attribute[0].length;
    state.posMax = max;
    return true;
  });

  markdown.block.ruler.before('fence', 'question_size_marker', (state, startLine, _endLine, silent) => {
    if (state.sCount[startLine] - state.blkIndent >= 4) return false;
    const line = state.src.slice(state.bMarks[startLine] + state.tShift[startLine], state.eMarks[startLine]);
    const isOpen = blockOpener.test(line);
    if (!isOpen && !blockCloser.test(line)) return false;
    if (silent) return true;
    const marker = state.push('question_size_marker', '', 0);
    marker.content = line;
    marker.map = [startLine, startLine + 1];
    const size = blockAttribute.exec(line)?.[1];
    marker.meta = { isOpen, size: size && isQuestionTextSize(size) ? size : undefined };
    state.line = startLine + 1;
    return true;
  }, { alt: ['paragraph', 'reference', 'blockquote', 'list'] });

  markdown.core.ruler.after('block', 'question_size_blocks', (state) => {
    const ancestry: number[] = [];
    const pending = new Map<string, Token[]>();
    const paired = new Map<Token, { size: string; nesting: 1 | -1; partner: Token }>();
    state.tokens.forEach((token, index) => {
      if (token.type === 'question_size_marker') {
        const context = ancestry.join('/');
        const openings = pending.get(context) ?? [];
        pending.set(context, openings);
        if (token.meta.isOpen) openings.push(token);
        else {
          const opening = openings.pop();
          if (opening?.meta.size) {
            paired.set(opening, { size: opening.meta.size, nesting: 1, partner: token });
            paired.set(token, { size: opening.meta.size, nesting: -1, partner: opening });
          }
        }
      } else if (token.nesting === 1) ancestry.push(index);
      else if (token.nesting === -1) ancestry.pop();
    });

    const accepted = new Set<Token>();
    let wrapperDepth = 0;
    state.tokens.forEach((token) => {
      const match = paired.get(token);
      if (!match) return;
      if (match.nesting === 1) {
        if (wrapperDepth < QUESTION_MARKDOWN_MAX_NESTING) {
          accepted.add(token);
          accepted.add(match.partner);
        }
        wrapperDepth += 1;
      } else wrapperDepth -= 1;
    });

    state.tokens = state.tokens.flatMap((token) => {
      if (token.type !== 'question_size_marker') return [token];
      const match = paired.get(token);
      if (match && accepted.has(token)) {
        const wrapper = new state.Token(`question_size_block_${match.nesting === 1 ? 'open' : 'close'}`, 'div', match.nesting);
        wrapper.block = true;
        wrapper.map = token.map;
        wrapper.level = token.level;
        if (match.nesting === 1) wrapper.attrSet('class', `text-${match.size}`);
        return [wrapper];
      }
      // Invalid, unclosed and excessive-depth markers remain visible text.
      const opening = new state.Token('paragraph_open', 'p', 1);
      const text = new state.Token('inline', '', 0);
      const closing = new state.Token('paragraph_close', 'p', -1);
      opening.block = text.block = closing.block = true;
      opening.level = closing.level = token.level;
      text.level = token.level + 1;
      opening.map = text.map = token.map;
      text.content = token.content;
      text.children = [];
      return [opening, text, closing];
    });
    let level = 0;
    state.tokens.forEach((token) => {
      if (token.nesting === -1) level -= 1;
      token.level = level;
      if (token.nesting === 1) level += 1;
    });
  });
};
